/**
 * Skeleton Component
 * Componente de carga moderno con shimmer effect
 */

import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
  variant?: "default" | "text" | "circular" | "rectangular";
  width?: string | number;
  height?: string | number;
  animation?: "pulse" | "shimmer" | "none";
}

export function Skeleton({
  className,
  variant = "default",
  width,
  height,
  animation = "shimmer",
}: SkeletonProps) {
  const variants = {
    default: "rounded-lg",
    text: "rounded h-4",
    circular: "rounded-full",
    rectangular: "rounded-none",
  };

  const animations = {
    pulse: "animate-pulse bg-gray-200",
    shimmer: cn(
      "bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200",
      "bg-[length:200%_100%] animate-shimmer"
    ),
    none: "bg-gray-200",
  };

  const style: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  return (
    <div
      className={cn(variants[variant], animations[animation], className)}
      style={style}
      aria-hidden="true"
    />
  );
}

// Skeleton presets para casos comunes
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? "60%" : "100%"}
        />
      ))}
    </div>
  );
}

export function SkeletonAvatar({ size = "md" }: { size?: "sm" | "md" | "lg" | "xl" }) {
  const sizes = {
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64,
  };

  return <Skeleton variant="circular" width={sizes[size]} height={sizes[size]} />;
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("p-4 bg-white rounded-xl border border-gray-100", className)}>
      <div className="flex items-center gap-3 mb-4">
        <SkeletonAvatar />
        <div className="flex-1 space-y-2">
          <Skeleton height={14} width="40%" />
          <Skeleton height={12} width="60%" />
        </div>
      </div>
      <SkeletonText lines={2} />
    </div>
  );
}

export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="w-full space-y-3">
      {/* Header */}
      <div className="flex gap-4 pb-3 border-b border-gray-100">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} height={16} className="flex-1" />
        ))}
      </div>
      
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 items-center py-2">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              height={colIndex === 0 ? 40 : 16}
              className={colIndex === 0 ? "w-10 !rounded-full" : "flex-1"}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
